import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { colors } from '../../../core';
import Heading from '../Heading';
import Icon from '../Icon';
import { FullScreen } from './FullScreen';
import { IconContainer } from './IconContainer';
import { NewTab } from './NewTab';

export interface PDFCardProps {
  title?: string;
  children: React.ReactNode;
  onDownload?: () => Promise<boolean> | void;
  onFullSize?: () => void;
  onNewTab?: () => void;
  onPrint?: () => void;
  expanded: boolean;
}

const Wrapper = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${({ theme }) => theme.pdfBorderColor || colors.smoke};
  border-radius: ${({ theme }) => theme.borderRadius || '3px'};
  display: block;
  margin-bottom: 16px;
  overflow: hidden;

  & > *:last-child {
    margin-bottom: 0;
  }
`;

const Title = styled.div`
  background-color: ${({ theme }) => theme.pdfPanelBackgroundColor || colors.pearl};
  padding: 16px;
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
`;

const Content = styled.div`
  padding: 0;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 9px;
`;

const PDFCard = ({
  title,
  onDownload,
  onFullSize,
  onNewTab,
  onPrint,
  expanded,
  children,
}: PDFCardProps) => {
  const [downloading, setDownloading] = useState<boolean>(false);

  const theme = useTheme();

  const handleDownload = async () => {
    if (downloading) {
      return;
    }
    setDownloading(true);
    onDownload && (await onDownload());
    setDownloading(false);
  };

  return (
    <Wrapper>
      {title ? (
        <Title>
          <Heading size={5} noMargin smallCaps={false}>
            {title}
          </Heading>
          <ButtonsContainer>
            {onDownload ? (
              <IconContainer
                onClick={handleDownload}
                borderColor={
                  downloading
                    ? theme.pdfIconDisabledColor || colors.primaryDisabled
                    : theme.pdfIconColor || colors.primary
                }
              >
                <Icon
                  type={'download'}
                  size={24}
                  color={
                    downloading
                      ? theme.pdfIconDisabledColor || colors.primaryDisabled
                      : theme.pdfIconColor || colors.primary
                  }
                />
              </IconContainer>
            ) : null}
            {onNewTab ? (
              <NewTab onClick={onNewTab} />
            ) : (
              <IconContainer onClick={onPrint}>
                <Icon type={'print'} size={26} color={theme.pdfIconColor || colors.primary} />
              </IconContainer>
            )}
            <FullScreen onClick={onFullSize} expanded={expanded} />
          </ButtonsContainer>
        </Title>
      ) : null}
      <Content>{children}</Content>
    </Wrapper>
  );
};

export default PDFCard;
